import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import messageService from '../../services/messageService';
import './AdminChatSettingsPage.css';

const AdminChatSettingsPage = () => {
  const [settings, setSettings] = useState({ softDeleteMonths: 6, permanentDeleteMonths: 12 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [message, setMessage] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convsLoading, setConvsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchConversations();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await messageService.getChatSettings();
      if (res) {
        setSettings({
          softDeleteMonths: res.softDeleteMonths || 6,
          permanentDeleteMonths: res.permanentDeleteMonths || 12
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load chat settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = useCallback(async () => {
    setConvsLoading(true);
    try {
      const data = await messageService.getAdminConversations();
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setConvsLoading(false);
    }
  }, []);

  const handleDeleteConversation = async (convId) => {
    if (!window.confirm('Permanently delete this conversation and all its messages? This cannot be undone.')) return;
    setDeletingId(convId);
    try {
      await messageService.adminDeleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      setMessage({ type: 'success', text: 'Conversation deleted successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete conversation' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (settings.permanentDeleteMonths <= settings.softDeleteMonths) {
      setMessage({ type: 'error', text: 'Permanent delete must be greater than soft delete period' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await messageService.updateChatSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Run chat cleanup? This will hide/delete old conversations based on settings.')) return;
    setCleaning(true);
    setMessage(null);
    try {
      const res = await messageService.runChatCleanup();
      setMessage({ type: 'success', text: `Cleanup complete: ${res.softHidden || 0} hidden, ${res.permanentlyDeleted || 0} deleted` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Cleanup failed' });
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-chat-settings">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-chat-settings">
        <h1>Chat Settings</h1>
        <p className="admin-chat-settings-desc">
          Configure auto-delete policies for conversations. Soft-deleted conversations are hidden from users 
          but remain in the database. Permanently deleted conversations are removed entirely.
        </p>

        {message && (
          <div className={`admin-chat-alert admin-chat-alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave} className="admin-chat-form">
          <div className="admin-chat-field">
            <label>Soft Delete After (months)</label>
            <input
              type="number"
              min="1"
              max="120"
              value={settings.softDeleteMonths}
              onChange={(e) => setSettings({ ...settings, softDeleteMonths: parseInt(e.target.value) || 1 })}
            />
            <span className="admin-chat-hint">Conversations older than this will be hidden from users</span>
          </div>

          <div className="admin-chat-field">
            <label>Permanent Delete After (months)</label>
            <input
              type="number"
              min="2"
              max="120"
              value={settings.permanentDeleteMonths}
              onChange={(e) => setSettings({ ...settings, permanentDeleteMonths: parseInt(e.target.value) || 2 })}
            />
            <span className="admin-chat-hint">Conversations older than this will be permanently deleted</span>
          </div>

          <div className="admin-chat-actions">
            <button type="submit" className="admin-chat-save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button type="button" className="admin-chat-cleanup-btn" onClick={handleCleanup} disabled={cleaning}>
              {cleaning ? 'Running...' : 'Run Cleanup Now'}
            </button>
          </div>
        </form>

        {/* Conversation Management */}
        <div className="admin-chat-convos">
          <div className="admin-chat-convos-header">
            <h2>Manage Conversations</h2>
            <button className="admin-chat-refresh-btn" onClick={fetchConversations} disabled={convsLoading}>
              {convsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          <p className="admin-chat-settings-desc">
            View and manually delete conversations. Deletion is permanent and removes all messages.
          </p>

          {conversations.length === 0 && !convsLoading ? (
            <p className="admin-chat-no-convos">No conversations found.</p>
          ) : (
            <div className="admin-chat-table-wrap">
              <table className="admin-chat-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Buyer</th>
                    <th>Seller</th>
                    <th>Messages</th>
                    <th>Last Activity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map(conv => (
                    <tr key={conv.id}>
                      <td>{conv.id}</td>
                      <td>{conv.buyer?.companyName || conv.buyer?.email || `User #${conv.buyerId}`}</td>
                      <td>{conv.seller?.companyName || conv.seller?.email || `User #${conv.sellerId}`}</td>
                      <td>{conv._count?.messages || 0}</td>
                      <td>{conv.updatedAt ? new Date(conv.updatedAt).toLocaleDateString() : '—'}</td>
                      <td>
                        <button
                          className="admin-chat-delete-btn"
                          onClick={() => handleDeleteConversation(conv.id)}
                          disabled={deletingId === conv.id}
                        >
                          {deletingId === conv.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminChatSettingsPage;
